import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Collapse,
  CircularProgress,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./learning-path-v2.module.scss";

type LearningPathAgentV2Props = {
  parsedContent: any;
};

type LearningPath = {
  learning_path_id: string;
  learning_path_name: string;
  description: string;
  skills_covered: string[];
  type: string;
  source: string[];
  link: string[] | null;
  duration_display: string | null;
};

type TrainingSearchData = {
  status: string;
  learning_paths: LearningPath[];
};

export default function LearningPathAgentV2({
  parsedContent,
}: LearningPathAgentV2Props): JSX.Element {
  const dispatch = useAppDispatch();
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [selectedPathId, setSelectedPathId] = useState<string | null>(null);

  // Get current agent and session info from Redux
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("sessionId");
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  // Parse the training search data
  let trainingData: TrainingSearchData | null = null;

  try {
    const finalAnswer = parsedContent?.finalAnswer;
    if (typeof finalAnswer === "string") {
      trainingData = JSON.parse(finalAnswer);
    } else if (parsedContent?.learning_paths) {
      trainingData = parsedContent as TrainingSearchData;
    } else if (finalAnswer?.learning_paths) {
      trainingData = finalAnswer;
    }
  } catch (error) {
    console.error("Error parsing training search data:", error);
  }

  const toggleExpand = (pathId: string) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(pathId)) {
        newSet.delete(pathId);
      } else {
        newSet.add(pathId);
      }
      return newSet;
    });
  };

  const handleSelectLearningPath = (pathId: string, pathName: string) => {
    if (!currentAgent) return;

    setSelectedPathId(pathId);

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    // Send the learning path ID as message
    const message = pathId;

    const payload: any = {
      agentId: currentAgent.id,
      message: message,
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    dispatch(sendMessageToAgent(payload));
  };

  const getTypeColor = (type: string): string => {
    const lower = type.toLowerCase();
    if (lower.includes("certification")) return "#8b5cf6";
    if (lower.includes("workshop") || lower.includes("f2f")) return "#f59e0b";
    if (lower.includes("course")) return "#3b82f6";
    if (lower.includes("video")) return "#ec4899";
    return "#6b7280";
  };

  const getTypeLabel = (type: string): string => {
    const lower = type.toLowerCase();
    if (lower.includes("f2f")) return "In-Person Workshop";
    if (lower.includes("workshop")) return "Workshop";
    if (lower.includes("certification")) return "Certification";
    if (lower.includes("video")) return "Video Course";
    if (lower.includes("course")) return "Online Course";
    return type;
  };

  if (!trainingData || !trainingData.learning_paths) {
    return (
      <Box className={styles.errorContainer}>
        <Typography color="error">
          Unable to load training search results. Please try again.
        </Typography>
      </Box>
    );
  }

  const learningPaths = trainingData.learning_paths;

  if (learningPaths.length === 0) {
    return (
      <Box className={styles.emptyContainer}>
        <InfoOutlinedIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
        <Typography sx={{ fontSize: "16px", color: "#6b7280" }}>
          No training opportunities found matching your criteria.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.trainingSearchContainer}>
      {/* Header Section */}
      <Box className={styles.headerSection}>
        <Box className={styles.headerIcon}>
          <SchoolIcon sx={{ fontSize: 32, color: "#00a888" }} />
        </Box>
        <Typography className={styles.headerTitle}>
          Your Personalized Learning Path
        </Typography>
        <Typography className={styles.headerSubtitle}>
          {learningPaths.length}{" "}
          {learningPaths.length === 1 ? "option" : "options"} available
        </Typography>
      </Box>

      {/* Training Paths Grid */}
      <Box className={styles.pathsGrid}>
        {learningPaths.map((path) => {
          const isExpanded = expandedCards.has(path.learning_path_id);
          const hasDescription = path.description && path.description.trim();
          const isSelected = selectedPathId === path.learning_path_id;

          return (
            <Card
              key={path.learning_path_id}
              className={`${styles.pathCard} ${isSelected ? styles.pathCardSelected : ""}`}
            >
              <CardContent className={styles.pathCardContent}>
                {/* Header Row */}
                <Box className={styles.pathHeader}>
                  <Typography className={styles.pathName}>
                    {path.learning_path_name}
                  </Typography>
                  {hasDescription && (
                    <IconButton
                      size="small"
                      onClick={() => toggleExpand(path.learning_path_id)}
                      className={styles.expandButton}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </Box>

                {/* Type and Source Chips */}
                <Box className={styles.metaRow}>
                  <Chip
                    label={getTypeLabel(path.type)}
                    size="small"
                    sx={{
                      backgroundColor: getTypeColor(path.type),
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "11px",
                    }}
                  />
                  {path.source && path.source.length > 0 && (
                    <Chip
                      label={path.source.join(", ")}
                      size="small"
                      className={styles.sourceChip}
                    />
                  )}
                  {path.duration_display && (
                    <Chip
                      label={path.duration_display}
                      size="small"
                      icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                      className={styles.durationChip}
                    />
                  )}
                </Box>

                {/* Description - Collapsible */}
                {hasDescription && (
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Typography className={styles.pathDescription}>
                      {path.description}
                    </Typography>
                  </Collapse>
                )}

                {/* Skills Covered */}
                {path.skills_covered && path.skills_covered.length > 0 && (
                  <Box className={styles.skillsBox}>
                    <Typography className={styles.skillsLabel}>
                      Skills covered:
                    </Typography>
                    <Box className={styles.skillsChips}>
                      {path.skills_covered.map((skill, idx) => (
                        <Chip
                          key={idx}
                          label={skill}
                          size="small"
                          className={styles.skillChip}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Select Learning Path Button */}
                <Box className={styles.selectButtonBox}>
                  <Button
                    variant="contained"
                    onClick={() =>
                      handleSelectLearningPath(
                        path.learning_path_id,
                        path.learning_path_name,
                      )
                    }
                    disabled={agentLoading || isSelected}
                    className={`${styles.selectButton} ${isSelected ? styles.selectButtonSelected : ""}`}
                    startIcon={
                      agentLoading && isSelected ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : isSelected ? (
                        <CheckCircleIcon />
                      ) : null
                    }
                  >
                    {isSelected ? "Selected" : "Select Learning Path"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
